import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { StatusBadge } from "../components/StatusBadge";
import { money, fmtDateAU, parseMoneyToCents } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

function Row({ label, value, strong, negative }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${strong ? "text-slate-900 font-medium" : "text-slate-500"}`}>{label}</span>
      <span className={`font-num ${strong ? "font-bold text-slate-900" : "text-slate-700"} ${negative ? "text-red-600" : ""}`}>{value}</span>
    </div>
  );
}

export default function PaymentCheck() {
  const navigate = useNavigate();
  const { id } = useParams();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const start = sp.get("start") || "2026-09-01";
  const end = sp.get("end") || "2026-09-30";
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ amount: "", reason: "", notes: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["payment-check", id, start, end],
    queryFn: async () => (await api.get(`/practices/${id}/payment-check?start=${start}&end=${end}`)).data,
  });

  const markExplained = async (paymentId) => {
    await api.post(`/payments/${paymentId}/explain`);
    qc.invalidateQueries();
    toast.success("Difference marked as explained");
  };

  const saveAdjustment = async () => {
    try {
      const disc = data.payments.find((p) => p.is_discrepancy && !p.explained);
      await api.post("/adjustments", {
        practice_id: id,
        payment_id: disc?.id || null,
        original_expected_cents: data.expected_cents,
        adjustment_amount_cents: parseMoneyToCents(adjForm.amount),
        reason: adjForm.reason || "adjustment",
        date: end,
        notes: adjForm.notes || null,
      });
      qc.invalidateQueries();
      setAdjOpen(false);
      setAdjForm({ amount: "", reason: "", notes: "" });
      toast.success("Adjustment recorded (original expected amount is unchanged)");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading || !data) return <AppShell><PageHeader title="Payment Check" onBack={() => navigate(-1)} /></AppShell>;

  const diff = data.difference_cents;
  const hasDiff = diff !== 0;
  const openDisc = data.payments.find((p) => p.is_discrepancy && !p.explained);

  return (
    <AppShell>
      <PageHeader title="Payment Check" subtitle={`${data.practice.name} · ${data.period_start_fmt}–${data.period_end_fmt}`} onBack={() => navigate(-1)} />
      <div className="px-5 pb-24 df-fade-in space-y-4">
        <div className="df-card p-5">
          <Row label={`Revenue basis`} value={money(data.revenue_basis_cents)} />
          {data.lab_deductions_cents > 0 && <Row label="Lab deductions" value={`−${money(data.lab_deductions_cents)}`} negative />}
          <Row label="Adjusted calculation base" value={money(data.calculation_base_cents)} strong />
          <Row label={`Agreement (${data.rate_label})`} value={data.method_label} />
          <div className="border-t border-slate-100 mt-2 pt-2">
            <Row label="Expected" value={money(data.expected_cents)} strong />
            <Row label="Received" value={money(data.received_cents)} strong />
            <div className="flex items-center justify-between py-2 mt-1 border-t border-slate-100">
              <span className="font-semibold text-slate-900">Difference</span>
              <span data-testid="check-difference" className={`font-num font-bold text-xl ${hasDiff ? "text-red-600" : "text-emerald-700"}`}>{money(diff)}</span>
            </div>
          </div>
          {hasDiff && (
            <div className="mt-1 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
              Payment differs from expected amount. The expected amount is never changed automatically — record an adjustment or mark the difference explained.
            </div>
          )}
        </div>

        {hasDiff && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="check-add-adjustment" onClick={() => setAdjOpen(true)} className="flex-1 h-11 rounded-xl">Record Adjustment</Button>
            {openDisc && (
              <Button data-testid="check-mark-explained" onClick={() => markExplained(openDisc.id)} className="flex-1 h-11 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">Mark Explained</Button>
            )}
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-2 px-1">Work records</h3>
          <div className="space-y-2">
            {data.entries.map((e) => (
              <div key={e.id} className="df-card p-4 flex items-center justify-between" data-testid={`check-entry-${e.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{fmtDateAU(e.date)}</div>
                  <div className="text-xs text-slate-500">Expected {money(e.expected_cents)} · Received {money(e.received_cents)}</div>
                </div>
                <StatusBadge status={e.status} />
              </div>
            ))}
          </div>
        </div>

        {data.adjustments?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2 px-1">Adjustments</h3>
            <div className="space-y-2">
              {data.adjustments.map((a) => (
                <div key={a.id} className="df-card p-4" data-testid={`check-adjustment-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">{a.reason}</span>
                    <span className="font-num font-semibold text-slate-900">{money(a.adjustment_amount_cents)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Original expected {money(a.original_expected_cents)} · unchanged · {fmtDateAU(a.date)}</div>
                  {a.notes && <div className="text-xs text-slate-500 mt-1">{a.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Record Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Adjustment amount</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
                <Input type="number" step="0.01" data-testid="adj-amount" value={adjForm.amount} onChange={(e) => setAdjForm((f) => ({ ...f, amount: e.target.value }))} className="h-11 rounded-xl pl-7 font-num" placeholder="e.g. -240.00" />
              </div>
            </div>
            <div><Label>Reason</Label><Input data-testid="adj-reason" value={adjForm.reason} onChange={(e) => setAdjForm((f) => ({ ...f, reason: e.target.value }))} className="mt-1.5 h-11 rounded-xl" placeholder="e.g. lab reconciliation" /></div>
            <div><Label>Notes</Label><Textarea data-testid="adj-notes" value={adjForm.notes} onChange={(e) => setAdjForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
            <p className="text-xs text-slate-500">Adjustments are stored as separate records. Your original expected amount is never overwritten.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="adj-save" onClick={saveAdjustment} className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">Save Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
